import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Settings2 } from "lucide-react";
import { PaymentCategory } from "@/hooks/payments/usePaymentCategories";

interface CategorySelectorProps {
  categories: PaymentCategory[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onManageClick: () => void;
  isLoading?: boolean;
}

export const CategorySelector = ({
  categories,
  selectedIds,
  onSelectionChange,
  onManageClick,
  isLoading,
}: CategorySelectorProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleToggle = (categoryId: string) => {
    if (selectedIds.includes(categoryId)) {
      onSelectionChange(selectedIds.filter((id) => id !== categoryId));
    } else {
      onSelectionChange([...selectedIds, categoryId]);
    }
  };

  const totalAmount = categories
    .filter((cat) => selectedIds.includes(cat.id))
    .reduce((sum, cat) => sum + cat.amount, 0);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-muted animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Categorías</label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onManageClick}
          className="h-8 px-2 text-muted-foreground hover:text-foreground"
        >
          <Settings2 className="h-4 w-4 mr-1" />
          Gestionar
        </Button>
      </div>

      {categories.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          No hay categorías configuradas.{" "}
          <button
            type="button"
            onClick={onManageClick}
            className="text-primary hover:underline"
          >
            Crear una
          </button>
        </p>
      ) : (
        <div className="space-y-2 border rounded-md p-3">
          {categories.map((category) => (
            <label
              key={category.id}
              className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedIds.includes(category.id)}
                  onCheckedChange={() => handleToggle(category.id)}
                />
                <span className="text-sm">{category.name}</span>
              </div>
              <span className="text-sm font-medium text-muted-foreground">
                {formatCurrency(category.amount)}
              </span>
            </label>
          ))}
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="text-sm font-medium">Total seleccionado:</span>
          <span className="text-lg font-bold text-primary">
            {formatCurrency(totalAmount)}
          </span>
        </div>
      )}
    </div>
  );
};
